import React, { useEffect, useState } from 'react';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const SkillBreakdownWidget = () => {
  const [ratings, setRatings] = useState(null);
  const [loading, setLoading] = useState(true);

  const skillLabels = [
    'Autonomy',
    'Competence',
    'Relatedness',
    'Feedback',
    'Listening',
    'Problem Solving',
    'Focus',
    'Inspiring',
    'Leadership Style',
    'Self-Awareness',
    'Other-Awareness',
    'Social Insight',
  ];

  const ratingKeys = [
    'autonomyRating',
    'competenceRating',
    'relatednessRating',
    'microskillsFeedbackRating',
    'activeListeningRating',
    'problemSolvingRating',
    'focusedRating',
    'inspiringRating',
    'styleSummaryRating',
    'emSelfRating',
    'emOtherRating',
    'socialUnderstanding',
  ];

  useEffect(() => {
    const fetchRatings = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/dashboard/skill-breakdown`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        });

        if (!res.ok) throw new Error('Failed to fetch skill breakdown');

        const data = await res.json();
        setRatings(data);
      } catch (err) {
        console.error('Error loading skill breakdown:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRatings();
  }, []);

  const skillAbbreviations = {
    'Autonomy': 'AU',
    'Competence': 'CO',
    'Relatedness': 'RE',
    'Feedback': 'FB',
    'Listening': 'LI',
    'Problem Solving': 'PS',
    'Focus': 'FO',
    'Inspiring': 'IN',
    'Leadership Style': 'LS',
    'Self-Awareness': 'SA',
    'Other-Awareness': 'OA',
    'Social Insight': 'SI'
  };

  const abbreviatedLabels = skillLabels.map(label => skillAbbreviations[label]);

  const dataPoints = ratingKeys.map(key => {
    const val = ratings?.[key];
    return typeof val === 'number' && !isNaN(val) ? val : null;
  });
  
  const validPoints = dataPoints.filter(val => val !== null);
  
  const chartData = validPoints.length > 0 ? {
    labels: abbreviatedLabels,
    datasets: [{
      label: 'Avg. Skill Scores (1–7)',
      data: dataPoints.map(val => val ?? 0),
      backgroundColor: 'rgba(59, 130, 246, 0.2)',
      borderColor: 'rgba(59, 130, 246, 1)',
      pointBackgroundColor: 'rgba(59, 130, 246, 1)',
      pointBorderColor: '#fff',
    }]
  } : null;

  return (
    <div className="relative bg-gray-100 shadow-inner rounded-lg p-6 overflow-hidden">
      {/* Background pattern */}
      <span
        aria-hidden="true"
        style={{
          backgroundImage: "url('/patterns/bubbles.svg')",
          backgroundSize: "250%",
          backgroundPosition: "10px 370px",
          opacity: 0.1,
        }}
        className="pointer-events-none absolute inset-0 bg-repeat z-0"
      />
  
      {/* Foreground content */}
      <div className="relative z-10">
        <h2 className="text-2xl font-bold text-gray-800 text-center mb-4">Skill Strength Breakdown</h2>
        <p className="text-sm text-gray-500 text-center mb-6">(Average of Last 30 Days of Reports)</p>
  
        {loading ? (
          <p className="text-sm text-gray-500 animate-pulse text-center">Loading chart...</p>
        ) : chartData ? (
          <div className="bg-white shadow-md rounded-lg p-6 h-[400px]">
            <Radar
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                animation: { duration: 0 },
                layout: { padding: { top: 0, bottom: 0 } },
                scales: {
                  r: {
                    min: 0,
                    max: 7,
                    ticks: {
                      stepSize: 1,
                      backdropColor: 'transparent',
                      color: '#4B5563',
                      font: { size: 12, family: 'Comic Sans MS, cursive, sans-serif' }
                    },
                    grid: {
                      color: '#E5E7EB',
                      lineWidth: 1.5
                    },
                    angleLines: {
                      color: '#E5E7EB',
                      lineWidth: 1.5
                    },
                    pointLabels: {
                      color: '#1F2937',
                      font: { size: 10, family: 'Arial, sans-serif' }
                    }
                  }
                },
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    backgroundColor: '#1F2937',
                    titleFont: { size: 14, family: 'Comic Sans MS, cursive, sans-serif' },
                    bodyFont: { size: 12, family: 'Comic Sans MS, cursive, sans-serif' },
                    borderColor: '#4B5563',
                    borderWidth: 1
                  }
                }
              }}
            />
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center">No feedback data available.</p>
        )}
  
        <div className="mt-4 text-sm text-gray-700">
          <ul className="grid grid-cols-2 gap-2">
            {Object.entries(skillAbbreviations).map(([full, abbr]) => (
              <li key={abbr} className="flex items-center">
                <span className="font-bold mr-2">{abbr}:</span> {full}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );  
};

export default SkillBreakdownWidget;
